/**
 * THERAPIST NOTES CRUD SERVICE
 *
 * Operaciones de creación, lectura, actualización y eliminación de apuntes
 * y cuadernos multi-página. Incluye chunking para RAG e indexación.
 * Extraído de therapist-notes.service.ts para mantener responsabilidad única.
 */

import { db } from '@/core/config/firebase';
import { Collections } from '@/core/config/collections';
import { Logger } from '@/core/utils';
import { auditLogService } from '@/core/security/audit-log.service';
import { secureDataService } from '@/core/security/secure-data.service';
import { biometricAuthService } from '@/core/services/native';
import { semanticLayerService } from '@/core/semantic';
import { documentIndexer, semanticTagsEngine } from '@/core/infrastructure';
import {
  TherapistNote,
  TherapistNoteChunk,
  CreateTherapistNoteDTO,
  UpdateTherapistNoteDTO,
  AddPageToNotebookDTO,
  NotebookPage,
  CHUNKING_CONFIG,
} from '@/core/types/models/therapist/therapist-notes.model';

const logger = Logger.getLogger('TherapistNotesCrudService');

const COLLECTIONS = {
  NOTES: Collections.THERAPIST_NOTES,
  CHUNKS: Collections.THERAPIST_NOTE_CHUNKS,
};

class TherapistNotesCrudService {

  // ============================================
  // SEGURIDAD
  // ============================================

  private async verifyBiometricAccess(): Promise<void> {
    try {
      const verified = await biometricAuthService.verifyIdentity({
        reason: 'Verifica tu identidad para acceder a notas clínicas',
        title: 'Acceso a Notas',
        subtitle: 'Datos sensibles protegidos',
      });
      if (!verified) throw new Error('Verificación de identidad requerida');
    } catch (e: any) {
      if (e.message?.includes('identidad') || e.message?.includes('Verificación')) throw e;
    }
  }

  // ============================================
  // CREAR APUNTE
  // ============================================

  async createNote(data: CreateTherapistNoteDTO): Promise<TherapistNote> {
    const now = new Date().toISOString();

    try {
      const { therapistNotesSettingsService } = await import('./therapist-notes-settings.service');
      await therapistNotesSettingsService.checkStorageQuota(data.therapistId, data.fileSizeBytes || 0);

      const analysis = semanticLayerService.analyzeContent(data.extractedText);

      // Encriptar texto clínico
      let textToStore = data.extractedText;
      let textEncrypted = false;
      let textEncryptionMetadata: any;

      if (data.extractedText) {
        const enc = await secureDataService.encryptSensitiveText(data.extractedText, data.therapistId);
        if (enc.encrypted) {
          textToStore = enc.data;
          textEncrypted = true;
          textEncryptionMetadata = enc.metadata;
        }
      }

      const isEmptyNotebook = data.sourceType === 'notebook' && !data.extractedText.trim();

      const noteData: Record<string, any> = {
        therapistId: data.therapistId,
        title: data.title,
        sourceType: data.sourceType,
        extractedText: textToStore,
        tags: data.tags || [],
        semanticTags: analysis.suggestedTags,
        detectedConcepts: analysis.detectedConcepts.slice(0, 10),
        status: isEmptyNotebook ? 'ready' : 'indexing',
        chunksCount: 0,
        fileSizeBytes: data.fileSizeBytes || 0,
        textCharacterCount: data.extractedText.length,
        createdAt: now,
        updatedAt: now,
      };

      if (textEncrypted) {
        noteData.textEncrypted = true;
        noteData.textEncryptionMetadata = textEncryptionMetadata;
      }
      if (data.originalFileUrl) noteData.originalFileUrl = data.originalFileUrl;
      if (data.originalFileName) noteData.originalFileName = data.originalFileName;
      if (data.summary) noteData.summary = data.summary;
      if (data.folderId) noteData.folderId = data.folderId;
      if (data.category) noteData.category = data.category;
      if (data.pages) noteData.pages = data.pages;
      if (data.pagesCount !== undefined) noteData.pagesCount = data.pagesCount;

      const docRef = await db.collection(COLLECTIONS.NOTES).add(noteData);
      const noteId = docRef.id;

      logger.info('Note created', { noteId, therapistId: data.therapistId });

      // Skip chunking and indexing for empty notebooks (no text yet)
      const hasText = data.extractedText.trim().length > 0;
      let chunksCount = 0;

      if (hasText) {
        const chunks = await this.createChunks(noteId, data.therapistId, data.title, data.extractedText, data.category);
        chunksCount = chunks.length;
      }

      await docRef.update({
        status: 'ready',
        chunksCount,
        updatedAt: new Date().toISOString()
      });

      await therapistNotesSettingsService.updateUsageMetrics(data.therapistId, {
        notesAdded: 1,
        chunksAdded: chunksCount,
        bytesAdded: data.fileSizeBytes || 0,
        charactersAdded: data.extractedText.length,
      });

      if (hasText) {
        const { therapistNotesOntologyService } = await import('./therapist-notes-ontology.service');
        await therapistNotesOntologyService.updateOntologyFromNote(data.therapistId, noteId, analysis);
      }

      return {
        id: noteId,
        ...noteData,
        extractedText: data.extractedText,
        status: 'ready',
        chunksCount,
      } as TherapistNote;

    } catch (error: any) {
      logger.error('Failed to create note', error, { therapistId: data.therapistId });
      throw error;
    }
  }

  // ============================================
  // LEER APUNTES
  // ============================================

  async getNotes(therapistId: string, options?: {
    folderId?: string;
    category?: string;
    tags?: string[];
    status?: TherapistNote['status'];
    limit?: number;
    orderBy?: 'createdAt' | 'updatedAt' | 'title';
    orderDir?: 'asc' | 'desc';
  }): Promise<TherapistNote[]> {
    try {
      await this.verifyBiometricAccess();

      let query: any = db.collection(COLLECTIONS.NOTES)
        .where('therapistId', '==', therapistId);

      if (options?.folderId) query = query.where('folderId', '==', options.folderId);
      if (options?.category) query = query.where('category', '==', options.category);
      if (options?.status) query = query.where('status', '==', options.status);

      const orderBy = options?.orderBy || 'createdAt';
      const orderDir = options?.orderDir || 'desc';
      query = query.orderBy(orderBy, orderDir);

      if (options?.limit) query = query.limit(options.limit);

      const snapshot = await query.get();

      const notes = await Promise.all(
        snapshot.docs.map(async (doc: any) => {
          const data = doc.data();
          let extractedText = data.extractedText;

          if (data.textEncrypted && data.textEncryptionMetadata) {
            extractedText = await secureDataService.decryptSensitiveText(
              data.extractedText, therapistId, data.textEncryptionMetadata
            );
          }

          return { id: doc.id, ...data, extractedText } as TherapistNote;
        })
      );

      auditLogService.log({
        userId: therapistId,
        userRole: 'therapist',
        action: 'view',
        resourceType: 'therapist_notes',
        resourceId: therapistId,
        details: { notesCount: notes.length, folderId: options?.folderId }
      }).catch(e => logger.error('Audit log failed', e));

      if (options?.tags && options.tags.length > 0) {
        return notes.filter(note =>
          options.tags!.some(tag => note.tags.includes(tag))
        );
      }

      return notes;

    } catch (error: any) {
      logger.warn('Failed to get notes, returning empty array', { therapistId, error: error.message });
      return [];
    }
  }

  async getNote(noteId: string, therapistId: string): Promise<TherapistNote | null> {
    try {
      await this.verifyBiometricAccess();

      const doc = await db.collection(COLLECTIONS.NOTES).doc(noteId).get();
      if (!doc.exists) return null;

      const data = doc.data() as any;

      if (data.therapistId !== therapistId) {
        logger.warn('Note access denied - wrong therapist', { noteId, therapistId });
        return null;
      }

      let extractedText = data.extractedText;
      if (data.textEncrypted && data.textEncryptionMetadata) {
        extractedText = await secureDataService.decryptSensitiveText(
          data.extractedText, therapistId, data.textEncryptionMetadata
        );
      }

      const note = { id: doc.id, ...data, extractedText } as TherapistNote;

      auditLogService.log({
        userId: therapistId,
        userRole: 'therapist',
        action: 'view',
        resourceType: 'therapist_note',
        resourceId: noteId,
        details: { noteTitle: note.title }
      }).catch(e => logger.error('Audit log failed', e));

      await doc.ref.update({ lastAccessedAt: new Date().toISOString() });

      return note;

    } catch (error: any) {
      logger.error('Failed to get note', error, { noteId });
      throw error;
    }
  }

  // ============================================
  // ACTUALIZAR Y ELIMINAR
  // ============================================

  async updateNote(noteId: string, therapistId: string, data: UpdateTherapistNoteDTO): Promise<void> {
    try {
      const noteRef = db.collection(COLLECTIONS.NOTES).doc(noteId);
      const doc = await noteRef.get();

      if (!doc.exists) throw new Error('Apunte no encontrado');

      const note = doc.data() as TherapistNote;
      if (note.therapistId !== therapistId) {
        throw new Error('No tienes permiso para editar este apunte');
      }

      await noteRef.update({ ...data, updatedAt: new Date().toISOString() });
      logger.info('Note updated', { noteId });

    } catch (error: any) {
      logger.error('Failed to update note', error, { noteId });
      throw error;
    }
  }

  async deleteNote(noteId: string, therapistId: string): Promise<void> {
    try {
      const noteRef = db.collection(COLLECTIONS.NOTES).doc(noteId);
      const doc = await noteRef.get();

      if (!doc.exists) throw new Error('Apunte no encontrado');

      const note = doc.data() as TherapistNote;
      if (note.therapistId !== therapistId) {
        throw new Error('No tienes permiso para eliminar este apunte');
      }

      const chunksSnapshot = await db.collection(COLLECTIONS.CHUNKS)
        .where('noteId', '==', noteId)
        .get();

      const batch = db.batch();
      chunksSnapshot.docs.forEach(chunkDoc => batch.delete(chunkDoc.ref));
      batch.delete(noteRef);
      await batch.commit();

      const { therapistNotesSettingsService } = await import('./therapist-notes-settings.service');
      await therapistNotesSettingsService.updateUsageMetrics(therapistId, {
        notesAdded: -1,
        chunksAdded: -note.chunksCount,
        bytesAdded: -note.fileSizeBytes,
        charactersAdded: -note.textCharacterCount,
      });

      logger.info('Note deleted', { noteId, chunksDeleted: chunksSnapshot.size });

    } catch (error: any) {
      logger.error('Failed to delete note', error, { noteId });
      throw error;
    }
  }

  // ============================================
  // CUADERNOS MULTI-PÁGINA
  // ============================================

  async createNotebook(data: {
    therapistId: string;
    title: string;
    folderId?: string;
    description?: string;
    tags?: string[];
    category?: string;
    firstPage?: Omit<NotebookPage, 'id' | 'order'>;
  }): Promise<TherapistNote> {
    const now = new Date().toISOString();

    let pages: NotebookPage[] = [];
    let extractedText = '';
    let fileSizeBytes = 0;
    let originalFileUrl: string | undefined;

    if (data.firstPage) {
      const pageId = `page_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const firstPage: NotebookPage = {
        id: pageId,
        order: 1,
        imageUrl: data.firstPage.imageUrl,
        extractedText: data.firstPage.extractedText || '',
        ocrConfidence: data.firstPage.ocrConfidence || 0,
        fileSizeBytes: data.firstPage.fileSizeBytes || 0,
        addedAt: now,
      };
      if (data.firstPage.thumbnailUrl) firstPage.thumbnailUrl = data.firstPage.thumbnailUrl;
      pages = [firstPage];
      extractedText = firstPage.extractedText;
      fileSizeBytes = firstPage.fileSizeBytes;
      originalFileUrl = firstPage.imageUrl;
    }

    return this.createNote({
      therapistId: data.therapistId,
      title: data.title,
      sourceType: 'notebook',
      extractedText,
      originalFileUrl,
      folderId: data.folderId,
      tags: data.tags,
      category: data.category,
      fileSizeBytes,
      pages,
      pagesCount: pages.length,
      summary: data.description,
    });
  }

  async addPageToNotebook(data: AddPageToNotebookDTO): Promise<TherapistNote> {
    const { noteId, therapistId, page } = data;
    const now = new Date().toISOString();

    try {
      const noteRef = db.collection(COLLECTIONS.NOTES).doc(noteId);
      const doc = await noteRef.get();

      if (!doc.exists) throw new Error('Cuaderno no encontrado');
      const notebook = { id: doc.id, ...doc.data() } as TherapistNote;
      if (notebook.therapistId !== therapistId) throw new Error('No tienes permiso para editar este cuaderno');
      if (notebook.sourceType !== 'notebook') throw new Error('Este apunte no es un cuaderno multi-página');

      const { therapistNotesSettingsService } = await import('./therapist-notes-settings.service');
      await therapistNotesSettingsService.checkStorageQuota(therapistId, page.fileSizeBytes);

      const existingPages = notebook.pages || [];
      const pageId = `page_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const newPage: NotebookPage = {
        id: pageId,
        order: existingPages.length + 1,
        imageUrl: page.imageUrl,
        extractedText: page.extractedText || '',
        ocrConfidence: page.ocrConfidence || 0,
        fileSizeBytes: page.fileSizeBytes || 0,
        addedAt: now,
      };
      if (page.thumbnailUrl) newPage.thumbnailUrl = page.thumbnailUrl;

      const updatedPages = [...existingPages, newPage];
      const allText = updatedPages
        .sort((a, b) => a.order - b.order)
        .map(p => `--- Página ${p.order} ---\n${p.extractedText}`)
        .join('\n\n');

      const analysis = semanticLayerService.analyzeContent(allText);
      const totalSize = updatedPages.reduce((acc, p) => acc + p.fileSizeBytes, 0);

      await noteRef.update({
        pages: updatedPages,
        pagesCount: updatedPages.length,
        extractedText: allText,
        textCharacterCount: allText.length,
        fileSizeBytes: totalSize,
        semanticTags: analysis.suggestedTags,
        detectedConcepts: analysis.detectedConcepts.slice(0, 10),
        status: 'indexing',
        updatedAt: now,
      });

      // Re-crear chunks
      const chunksSnapshot = await db.collection(COLLECTIONS.CHUNKS)
        .where('noteId', '==', noteId).get();
      const batch = db.batch();
      chunksSnapshot.docs.forEach(chunkDoc => batch.delete(chunkDoc.ref));
      await batch.commit();

      const chunks = await this.createChunks(noteId, therapistId, notebook.title, allText, notebook.category);
      await noteRef.update({ status: 'ready', chunksCount: chunks.length });

      await therapistNotesSettingsService.updateUsageMetrics(therapistId, {
        bytesAdded: page.fileSizeBytes,
        charactersAdded: page.extractedText.length,
        chunksAdded: chunks.length - notebook.chunksCount,
      });

      const { therapistNotesOntologyService } = await import('./therapist-notes-ontology.service');
      await therapistNotesOntologyService.updateOntologyFromNote(therapistId, noteId, analysis);

      return {
        ...notebook,
        pages: updatedPages,
        pagesCount: updatedPages.length,
        extractedText: allText,
        status: 'ready',
        chunksCount: chunks.length,
        updatedAt: now,
      };

    } catch (error: any) {
      logger.error('Failed to add page to notebook', error, { noteId });
      throw error;
    }
  }

  async deletePageFromNotebook(noteId: string, therapistId: string, pageId: string): Promise<TherapistNote> {
    const now = new Date().toISOString();

    try {
      const noteRef = db.collection(COLLECTIONS.NOTES).doc(noteId);
      const doc = await noteRef.get();
      if (!doc.exists) throw new Error('Cuaderno no encontrado');

      const notebook = { id: doc.id, ...doc.data() } as TherapistNote;
      if (notebook.therapistId !== therapistId) throw new Error('No tienes permiso');
      if (notebook.sourceType !== 'notebook' || !notebook.pages) throw new Error('No es cuaderno');

      const pageToDelete = notebook.pages.find(p => p.id === pageId);
      if (!pageToDelete) throw new Error('Página no encontrada');

      if (notebook.pages.length === 1) {
        await this.deleteNote(noteId, therapistId);
        throw new Error('NOTEBOOK_DELETED');
      }

      const updatedPages = notebook.pages
        .filter(p => p.id !== pageId)
        .map((p, i) => ({ ...p, order: i + 1 }));

      const allText = updatedPages
        .sort((a, b) => a.order - b.order)
        .map(p => `--- Página ${p.order} ---\n${p.extractedText}`)
        .join('\n\n');

      const totalSize = updatedPages.reduce((acc, p) => acc + p.fileSizeBytes, 0);

      await noteRef.update({
        pages: updatedPages,
        pagesCount: updatedPages.length,
        extractedText: allText,
        textCharacterCount: allText.length,
        fileSizeBytes: totalSize,
        updatedAt: now,
      });

      const { therapistNotesSettingsService } = await import('./therapist-notes-settings.service');
      await therapistNotesSettingsService.updateUsageMetrics(therapistId, {
        bytesAdded: -pageToDelete.fileSizeBytes,
        charactersAdded: -pageToDelete.extractedText.length,
      });

      return { ...notebook, pages: updatedPages, pagesCount: updatedPages.length, extractedText: allText, updatedAt: now };

    } catch (error: any) {
      if (error.message === 'NOTEBOOK_DELETED') throw error;
      logger.error('Failed to delete page from notebook', error, { noteId, pageId });
      throw error;
    }
  }

  async updatePageText(noteId: string, therapistId: string, pageId: string, newText: string): Promise<TherapistNote> {
    const now = new Date().toISOString();

    try {
      const noteRef = db.collection(COLLECTIONS.NOTES).doc(noteId);
      const doc = await noteRef.get();
      if (!doc.exists) throw new Error('Cuaderno no encontrado');

      const notebook = { id: doc.id, ...doc.data() } as TherapistNote;
      if (notebook.therapistId !== therapistId) throw new Error('No tienes permiso');
      if (notebook.sourceType !== 'notebook' || !notebook.pages) throw new Error('No es cuaderno');

      const updatedPages = notebook.pages.map(p =>
        p.id === pageId ? { ...p, extractedText: newText } : p
      );

      const allText = updatedPages
        .sort((a, b) => a.order - b.order)
        .map(p => `--- Página ${p.order} ---\n${p.extractedText}`)
        .join('\n\n');

      await noteRef.update({
        pages: updatedPages,
        extractedText: allText,
        textCharacterCount: allText.length,
        updatedAt: now,
      });

      return { ...notebook, pages: updatedPages, extractedText: allText, updatedAt: now };

    } catch (error: any) {
      logger.error('Failed to update page text', error, { noteId, pageId });
      throw error;
    }
  }

  async reorderPages(noteId: string, therapistId: string, pageIds: string[]): Promise<TherapistNote> {
    const now = new Date().toISOString();

    try {
      const noteRef = db.collection(COLLECTIONS.NOTES).doc(noteId);
      const doc = await noteRef.get();
      if (!doc.exists) throw new Error('Cuaderno no encontrado');

      const notebook = { id: doc.id, ...doc.data() } as TherapistNote;
      if (notebook.therapistId !== therapistId) throw new Error('No tienes permiso');
      if (notebook.sourceType !== 'notebook' || !notebook.pages) throw new Error('No es cuaderno');

      const reorderedPages = pageIds.map((pageId, index) => {
        const page = notebook.pages!.find(p => p.id === pageId);
        if (!page) throw new Error(`Página ${pageId} no encontrada`);
        return { ...page, order: index + 1 };
      });

      const allText = reorderedPages
        .map(p => `--- Página ${p.order} ---\n${p.extractedText}`)
        .join('\n\n');

      await noteRef.update({ pages: reorderedPages, extractedText: allText, updatedAt: now });

      return { ...notebook, pages: reorderedPages, extractedText: allText, updatedAt: now };

    } catch (error: any) {
      logger.error('Failed to reorder pages', error, { noteId });
      throw error;
    }
  }

  // ============================================
  // CHUNKING PARA RAG
  // ============================================

  async createChunks(
    noteId: string,
    therapistId: string,
    noteTitle: string,
    text: string,
    noteCategory?: string
  ): Promise<TherapistNoteChunk[]> {
    const chunks: TherapistNoteChunk[] = [];
    const now = new Date().toISOString();

    const rawChunks = this.splitTextIntoChunks(text);
    const batch = db.batch();

    for (let i = 0; i < rawChunks.length; i++) {
      const chunkText = rawChunks[i];
      const analysis = semanticLayerService.analyzeContent(chunkText);
      const keywords = semanticLayerService.generateIndexKeywords(chunkText);

      const chunkData: Record<string, any> = {
        noteId,
        therapistId,
        content: chunkText,
        chunkIndex: i,
        noteTitle,
        semanticTags: analysis.suggestedTags.slice(0, 5),
        keywords: keywords.slice(0, 15),
        hasEmbedding: false,
        createdAt: now,
      };

      if (noteCategory) chunkData.noteCategory = noteCategory;

      const chunkRef = db.collection(COLLECTIONS.CHUNKS).doc();
      batch.set(chunkRef, chunkData);

      chunks.push({ id: chunkRef.id, ...chunkData } as TherapistNoteChunk);
    }

    await batch.commit();
    logger.info('Chunks created', { noteId, count: chunks.length });

    this.indexChunksInBackground(chunks).catch((error) => {
      logger.warn('Background embedding indexing failed', { noteId, error: error.message });
    });

    return chunks;
  }

  private async indexChunksInBackground(chunks: TherapistNoteChunk[]): Promise<void> {
    try {
      const documents = chunks.map(chunk => ({
        id: chunk.id,
        collectionName: COLLECTIONS.CHUNKS,
        title: chunk.noteTitle,
        content: chunk.content,
        metadata: {
          noteId: chunk.noteId,
          therapistId: chunk.therapistId,
          chunkIndex: chunk.chunkIndex,
        },
      }));

      const contentResult = await documentIndexer.indexBatch(documents);
      logger.info('Chunks indexed with content embeddings', {
        noteId: chunks[0]?.noteId,
        indexed: contentResult.successful,
        failed: contentResult.failed,
      });

      const taggableDocuments = chunks
        .filter(chunk => chunk.semanticTags && chunk.semanticTags.length > 0)
        .map(chunk => ({
          id: chunk.id,
          collectionName: COLLECTIONS.CHUNKS,
          semanticTags: chunk.semanticTags || [],
          hasTagsEmbedding: false,
        }));

      if (taggableDocuments.length > 0) {
        const tagsResult = await semanticTagsEngine.indexBatchTags(taggableDocuments);
        logger.info('Chunks indexed with tags embeddings', {
          noteId: chunks[0]?.noteId,
          indexed: tagsResult.successful,
          failed: tagsResult.failed,
        });
      }

    } catch (error: any) {
      logger.warn('Failed to index chunks with embeddings', {
        noteId: chunks[0]?.noteId,
        error: error.message,
      });
    }
  }

  private splitTextIntoChunks(text: string): string[] {
    const { maxChunkSize, overlapSize, minChunkSize, separators } = CHUNKING_CONFIG;
    const chunks: string[] = [];

    if (text.length <= maxChunkSize) {
      return [text.trim()];
    }

    let currentText = text;

    while (currentText.length > 0) {
      if (currentText.length <= maxChunkSize) {
        if (currentText.trim().length >= minChunkSize) {
          chunks.push(currentText.trim());
        }
        break;
      }

      let cutPoint = maxChunkSize;

      for (const separator of separators) {
        const searchArea = currentText.substring(
          Math.max(0, maxChunkSize - 100),
          maxChunkSize + 50
        );
        const sepIndex = searchArea.lastIndexOf(separator);

        if (sepIndex !== -1) {
          cutPoint = maxChunkSize - 100 + sepIndex + separator.length;
          break;
        }
      }

      const chunk = currentText.substring(0, cutPoint).trim();
      if (chunk.length >= minChunkSize) {
        chunks.push(chunk);
      }

      currentText = currentText.substring(Math.max(0, cutPoint - overlapSize));
    }

    return chunks;
  }
}

export const therapistNotesCrudService = new TherapistNotesCrudService();
